import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { Users, ShoppingCart, Settings, Activity, TrendingUp } from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const timeRanges = [
  { id: '1m', label: '1 Min', value: 1 },
  { id: '2m', label: '2 Min', value: 2 },
  { id: '5m', label: '5 Min', value: 5 },
  { id: '15m', label: '15 Min', value: 15 },
  { id: '30m', label: '30 Min', value: 30 },
  { id: '1h', label: '1 Hour', value: 60 },
  { id: '5h', label: '5 Hours', value: 300 },
  { id: '10h', label: '10 Hours', value: 600 },
  { id: '12h', label: '12 Hours', value: 720 },
  { id: '24h', label: '24 Hours', value: 1440 },
  { id: 'daily', label: 'Daily', value: 'daily' },
  { id: 'weekly', label: 'Weekly', value: 'weekly' },
  { id: 'monthly', label: 'Monthly', value: 'monthly' },
  { id: 'yearly', label: 'Yearly', value: 'yearly' },
  { id: 'all', label: 'All Time', value: 'all' },
];

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center">
    <div className={`p-4 rounded-lg ${color} mr-4`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    users: 0,
    orders: 0,
    settings: 0,
    buyOrders: 0,
    sellOrders: 0,
    allOrders: [],
    analytics: [],
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('daily');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [usersRes, ordersRes, settingsRes] = await Promise.all([
          api.get('/api/admin/users'),
          api.get('/api/admin/orders'),
          api.get('/api/admin/settings'),
        ]);
        
        const getList = (res) => {
          const d = res.data;
          if (Array.isArray(d)) return d;
          if (d.data && Array.isArray(d.data)) return d.data;
          if (d.users && Array.isArray(d.users)) return d.users;
          if (d.orders && Array.isArray(d.orders)) return d.orders;
          if (d.data && d.data.data && Array.isArray(d.data.data)) return d.data.data;
          if (d.data && d.data.users && Array.isArray(d.data.users)) return d.data.users;
          if (d.data && d.data.orders && Array.isArray(d.data.orders)) return d.data.orders;
          return [];
        };

        const userData = getList(usersRes);
        const orderData = getList(ordersRes);
        const settingsRaw = settingsRes.data.data || settingsRes.data;
        const settingsData = Array.isArray(settingsRaw) ? settingsRaw : (settingsRaw.settings || settingsRaw);

        setStats(prev => ({
          ...prev,
          users: usersRes.data.total || usersRes.data.data?.total || userData.length,
          orders: ordersRes.data.total || ordersRes.data.data?.total || orderData.length,
          settings: Object.keys(settingsData).length || 0,
          allOrders: orderData,
        }));
      } catch (err) {
        console.error('Failed to fetch stats', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    if (stats.allOrders.length === 0) return;

    const calculateAnalytics = () => {
      const dailyData = {};
      let buys = 0;
      let sells = 0;

      const selectedRange = timeRanges.find(r => r.id === timeRange);

      stats.allOrders.forEach(order => {
        const dateObj = new Date(order.created_at);
        const timestamp = dateObj.getTime();
        let label;
        let bucketTimestamp = timestamp;
        
        if (typeof selectedRange.value === 'number') {
          // Minute/Hour buckets
          const intervalMs = selectedRange.value * 60 * 1000;
          bucketTimestamp = Math.floor(timestamp / intervalMs) * intervalMs;
          const bucketDate = new Date(bucketTimestamp);
          
          if (selectedRange.value < 60) {
            label = bucketDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } else if (selectedRange.value < 1440) {
            label = `${bucketDate.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${bucketDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
          } else {
            label = bucketDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
          }
        } else if (timeRange === 'daily') {
          label = dateObj.toLocaleDateString();
          bucketTimestamp = new Date(dateObj.toDateString()).getTime();
        } else if (timeRange === 'weekly') {
          const firstDay = new Date(dateObj.setDate(dateObj.getDate() - dateObj.getDay()));
          label = `Week of ${firstDay.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
          bucketTimestamp = firstDay.getTime();
        } else if (timeRange === 'monthly') {
          label = dateObj.toLocaleString('default', { month: 'long', year: 'numeric' });
          bucketTimestamp = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1).getTime();
        } else if (timeRange === 'yearly') {
          label = dateObj.getFullYear().toString();
          bucketTimestamp = new Date(dateObj.getFullYear(), 0, 1).getTime();
        } else {
          label = 'All Time';
          bucketTimestamp = 0;
        }

        if (!dailyData[label]) {
          dailyData[label] = { buy: 0, sell: 0, total: 0, timestamp: bucketTimestamp };
        }
        
        const type = (order.type || order.side || 'buy').toLowerCase();
        if (type === 'buy') {
          dailyData[label].buy += 1;
          buys += 1;
        } else {
          dailyData[label].sell += 1;
          sells += 1;
        }
        dailyData[label].total += 1;
      });

      const analytics = Object.keys(dailyData).map(label => ({
        label,
        ...dailyData[label]
      })).sort((a, b) => a.timestamp - b.timestamp);

      // Limit results except for 'all'
      const limitedAnalytics = timeRange === 'all' ? analytics : analytics.slice(-20); // Take last 20 for graph

      setStats(prev => ({
        ...prev,
        buyOrders: buys,
        sellOrders: sells,
        analytics: limitedAnalytics,
      }));
    };

    calculateAnalytics();
  }, [timeRange, stats.allOrders]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">System Overview</h1>
        <p className="text-gray-500">Monitor your platform's key performance indicators.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        <StatCard 
          title="Total Users" 
          value={loading ? '...' : stats.users} 
          icon={Users} 
          color="bg-blue-500" 
        />
        <StatCard 
          title="Total Orders" 
          value={loading ? '...' : stats.orders} 
          icon={ShoppingCart} 
          color="bg-purple-500" 
        />
        <StatCard 
          title="Buy Orders" 
          value={loading ? '...' : stats.buyOrders} 
          icon={TrendingUp} 
          color="bg-green-500" 
        />
        <StatCard 
          title="Sell Orders" 
          value={loading ? '...' : stats.sellOrders} 
          icon={Activity} 
          color="bg-red-500" 
        />
        <StatCard 
          title="Configured Settings" 
          value={loading ? '...' : stats.settings} 
          icon={Settings} 
          color="bg-orange-500" 
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <h2 className="text-lg font-bold text-gray-800 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-indigo-500" />
              Buy/Sell Analytics
            </h2>
            <div className="flex bg-gray-100 p-1 rounded-lg overflow-x-auto max-w-full sm:max-w-md lg:max-w-lg scrollbar-hide">
              {timeRanges.map((range) => (
                <button
                  key={range.id}
                  onClick={() => setTimeRange(range.id)}
                  className={`px-3 py-1.5 text-[10px] font-bold rounded-md transition-all whitespace-nowrap ${
                    timeRange === range.id 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-[300px] w-full">
            {loading ? (
              <div className="animate-pulse flex items-center justify-center h-full bg-gray-50 rounded-lg">
                <Activity className="w-8 h-8 text-gray-200 animate-spin" />
              </div>
            ) : stats.analytics.length === 0 ? (
              <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
                <p className="text-gray-500 text-sm italic">No transaction data available yet.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.analytics} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="label" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 10 }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                    cursor={{ fill: '#f9fafb' }}
                  />
                  <Legend iconType="circle" />
                  <Bar name="Buy" dataKey="buy" fill="#22c55e" radius={[4, 4, 0, 0]} barSize={20} />
                  <Bar name="Sell" dataKey="sell" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
              System Status
            </h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-600">API Server</span>
              <span className="px-3 py-1 text-xs font-bold bg-green-100 text-green-800 rounded-full flex items-center">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                Operational
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-600">Database</span>
              <span className="px-3 py-1 text-xs font-bold bg-green-100 text-green-800 rounded-full flex items-center">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                Operational
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-600">Order Matching</span>
              <span className="px-3 py-1 text-xs font-bold bg-green-100 text-green-800 rounded-full flex items-center">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
